import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { useAppSelector, useAppDispatch } from 'store';
import { ChatText, Header, Loader, Sidebar, SidebarEmpty } from 'features/technographics';
import { Icon, Text } from 'shared/components/ui';
import { setThreadId, addMessage, setMessages } from 'store/messages';

import styles from './index.module.scss';

interface Message {
	id: string;
	text: string;
	sender: 'user' | 'bot';
	timestamp: Date;
}

const UserMessage = ({ value }: { value: string }) => {
	return (
		<div className={styles.userMessage}>
			<Text variant="inter/16/regular">{value}</Text>
		</div>
	);
};

const AssistantMessage = ({ value }: { value: string }) => {
	if (!value.includes(':'))
		return (
			<div className={styles.assistantMessage}>
				<Text variant="inter/16/semi">{value}</Text>
			</div>
		);

	const lines = value.split('\n');
	return (
		<div className={styles.assistantMessage}>
			<Text variant="inter/16/regular">
				{lines.map((line, index) => {
					const parts = line.split(/\s*:\s*/);
					const isRefinementTitle = line.trim().toLowerCase() === 'refinement options';
					const isLastLine = index === lines.length - 1;
					const isEmptyLine = line.trim() === '';
					return (
						<span key={index}>
							{isRefinementTitle ? (
								<>
									<br />
									<span className={styles.bold}>{line}</span>
								</>
							) : parts.length === 2 ? (
								<>
									<span className={styles.bold}>{parts[0]}</span> : {parts[1]}
								</>
							) : (
								line
							)}
							{!isLastLine && !isEmptyLine && <br />}
						</span>
					);
				})}
			</Text>
		</div>
	);
};

interface Props {
	sendMessage: (userMessage: string) => Promise<void>;
	isLoading: boolean;
	tags: string[];
	setTags: React.Dispatch<React.SetStateAction<string[]>>;
	buttonsDisabled: boolean;
	setButtonsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ChatWithMessages = ({
	sendMessage,
	isLoading,
	tags,
	setTags,
	buttonsDisabled,
	setButtonsDisabled
}: Props) => {
	const navigate = useNavigate();
	const [value, setValue] = useState<string>('');
	const messagesEndRef = useRef<HTMLDivElement | null>(null);

	const dispatch = useAppDispatch();
	const { messages } = useAppSelector(state => state.messages);

	const handleChange = (value: string) => {
		setValue(value);
	};

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const handleSubmit = () => {
		if (buttonsDisabled) return;
		if (!value) return;
		setButtonsDisabled(true);

		const userMessage: Message = {
			id: Date.now().toString(),
			text: value,
			sender: 'user',
			timestamp: new Date()
		};
		dispatch(addMessage(userMessage));
		setValue('');
		sendMessage(userMessage.text);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	const handleNext = () => {
		navigate(`/technographics/search?tags=${encodeURIComponent(tags?.join(',')!)}`);
	};

	const handleFinalize = () => {
		if (buttonsDisabled) return;
		setButtonsDisabled(true);

		const userMessage: Message = {
			id: Date.now().toString(),
			text: 'Finalise',
			sender: 'user',
			timestamp: new Date()
		};
		dispatch(addMessage(userMessage));
		setValue('');
		sendMessage(userMessage.text);
	};

	const handleBack = () => {
		setValue('');
		dispatch(setMessages([]));
		setTags([]);
		dispatch(setThreadId(null));
	};

	return (
		<>
			<Header progress={'tags'} onClick={handleBack} />
			<div className={styles.container}>
				<div className={styles.wrapper}>
					<div className={styles.title}>
						<Text variant="inter/28/semi">Build a list of technology tags</Text>
						<Text variant="inter/16/regular" color="grey">
							Describe the technologies you're looking for and we'll help you build the perfect search
						</Text>
					</div>
					<div className={styles.windowWrapper}>
						<div className={styles.window}>
							<div className={styles.messages}>
								{messages.map(message =>
									message.sender === 'user' ? (
										<UserMessage value={message.text} key={message.text} />
									) : (
										<AssistantMessage value={message.text} key={message.text} />
									)
								)}
								{isLoading && (
									<div className={styles.loader}>
										<Loader />
									</div>
								)}
								<div ref={messagesEndRef} />
							</div>
							<ChatText
								className={styles.chatInput}
								value={value}
								onChange={handleChange}
								variant="chat"
								onKeyDown={handleKeyDown}
								suffix={
									<div className={styles.inline}>
										<div
											className={buttonsDisabled ? styles.buttonDisabled : styles.button}
											onClick={handleFinalize}>
											<Text variant="inter/15/semi" color={buttonsDisabled ? 'grey' : 'white'}>
												Finalise skills
											</Text>
										</div>
										<div
											onClick={handleSubmit}
											className={cn(styles.arrow, { [styles.arrowDisabled]: buttonsDisabled })}>
											<Icon icon="arrow-left" size={24} className={styles.arrowIcon} />
										</div>
									</div>
								}
							/>
						</div>
						{!!tags && tags.length > 0 ? (
							<div className={styles.sidebarWrapper}>
								<Sidebar
									tags={tags}
									setTags={setTags}
									handleNext={handleNext}
									textNext="Continue to Filters"
								/>
							</div>
						) : (
							<div className={cn(styles.sidebarWrapper, styles.sidebarWrapperEmpty)}>
								<SidebarEmpty />
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};
